if (!customElements.get('video-reel-widget')) {
  class VideoReelWidget extends HTMLElement {
    constructor() {
      super();
      this.formatCount = this.formatCount.bind(this);
      this.counters = {};
      this.remote = false;
    }

    connectedCallback() {
      const isBubble = !!this.querySelector('[data-reel-bubble-video]');
      if (isBubble && this.parentElement !== document.body) {
        document.body.appendChild(this);
        const bubbleEl = this.querySelector('.m-reel-bubble');
        if (bubbleEl) {
          bubbleEl.style.position = 'fixed';
          bubbleEl.style.zIndex = '2147483000';
        }
        return;
      }

      this.modal = this.querySelector('[data-reel-modal]');
      this.track = this.querySelector('[data-reel-track]');
      this.strip = this.querySelector('[data-reel-strip]');
      this.closeBtn = this.querySelector('[data-reel-close]');
      this.prevBtn = this.querySelector('[data-reel-prev]');
      this.nextBtn = this.querySelector('[data-reel-next]');
      this.slides = Array.from(this.querySelectorAll('[data-reel-slide]'));

      // The counters stay hidden (see video-reel-widget.css) until the backend
      // answered or failed, so the merchant's initial values never flash
      // before the real ones.
      this.ready = this.hydrateCounts().finally(() => this.setAttribute('data-counters-ready', ''));

      this.querySelectorAll('[data-reel-open]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const index = parseInt(trigger.getAttribute('data-reel-open'), 10) || 0;
          this.openModal(index);
        });
      });

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.modal.close());
      }
      if (this.modal) {
        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) this.modal.close();
        });
        this.modal.addEventListener('close', () => this.pauseAllVideos());
      }
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

      this.slides.forEach((slide, index) => {
        const video = slide.querySelector('[data-reel-video]');
        const likeBtn = slide.querySelector('[data-reel-like]');
        if (video) {
          video.addEventListener('error', () => {
            console.warn('[video-reel] video failed to load', video.currentSrc || video.src, video.error);
          });
          video.addEventListener('click', () => this.togglePlay(video));
          video.muted = true;
          video.addEventListener('play', () => {
            this.pauseAllVideos(video);
            this.registerView(slide);
            this.syncControls(slide);
          });
          video.addEventListener('pause', () => this.syncControls(slide));
          video.addEventListener('volumechange', () => this.syncControls(slide));
          const muteBtn = slide.querySelector('[data-reel-mute]');
          const playBtn = slide.querySelector('[data-reel-playpause]');
          const stopBtn = slide.querySelector('[data-reel-stop]');
          if (muteBtn) muteBtn.addEventListener('click', () => { video.muted = !video.muted; });
          if (playBtn) playBtn.addEventListener('click', () => this.togglePlay(video));
          if (stopBtn) {
            stopBtn.addEventListener('click', () => {
              video.pause();
              video.currentTime = 0;
            });
          }
          this.syncControls(slide);
        }
        if (likeBtn) {
          likeBtn.addEventListener('click', () => this.toggleLike(slide, likeBtn));
        }
        const cartBtn = slide.querySelector('[data-reel-add-to-cart]');
        if (cartBtn) {
          cartBtn.addEventListener('click', () => this.addToCart(cartBtn));
        }
      });

      this.setupBubble();
      this.setupStripControls();
      this.setupAutoplayStrip();
      this.setupActiveSlideObserver();
    }

    formatCount(n) {
      n = parseInt(n, 10) || 0;
      if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
      if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
      return String(n);
    }

    get apiBase() {
      return (this.getAttribute('data-counters-api') || '').trim().replace(/\/+$/, '');
    }

    getVisitorId() {
      if (VideoReelWidget.visitorId) return VideoReelWidget.visitorId;
      let id = null;
      try {
        id = localStorage.getItem('mreel:visitor');
      } catch (e) {}
      if (!id || !/^[A-Za-z0-9_-]{8,64}$/.test(id)) {
        id =
          window.crypto && typeof window.crypto.randomUUID === 'function'
            ? window.crypto.randomUUID()
            : 'v' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
        try {
          localStorage.setItem('mreel:visitor', id);
        } catch (e) {}
      }
      VideoReelWidget.visitorId = id;
      return id;
    }

    // Ids the backend accepts. Anything else stays local-only.
    isRemoteId(id) {
      return /^[A-Za-z0-9_\-:.]{1,128}$/.test(id || '');
    }

    async api(path, options) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 4000);
      try {
        const res = await fetch(this.apiBase + path, {
          ...options,
          headers: options && options.body ? { 'Content-Type': 'application/json' } : undefined,
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('counters api ' + res.status);
        return await res.json();
      } finally {
        clearTimeout(timer);
      }
    }

    // Merchant-entered initial value: the backend rejects negatives and
    // anything above the largest exact JS integer, and one bad item would fail
    // the whole batch.
    baseValue(raw) {
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? Math.min(Math.max(n, 0), Number.MAX_SAFE_INTEGER) : 0;
    }

    localLiked(id) {
      try {
        return localStorage.getItem('mreel:' + id + ':liked') === '1';
      } catch (e) {
        return false;
      }
    }

    // Shows the values from the HTML (data-base-*) right away, then asks the
    // backend to seed them (the greater value wins) and returns the shared
    // counts. Any failure leaves the base values on screen.
    async hydrateCounts() {
      this.slides.forEach((slide) => {
        const id = slide.getAttribute('data-id');
        const state = {
          views: this.baseValue(slide.getAttribute('data-base-views')),
          likes: this.baseValue(slide.getAttribute('data-base-likes')),
          liked: this.localLiked(id),
          pending: false,
        };
        this.counters[id] = state;
        this.renderCounts(id, state.views, state.likes, state.liked);
      });

      if (!this.apiBase) return;
      const ids = Object.keys(this.counters).filter((id) => this.isRemoteId(id));
      if (!ids.length) return;
      try {
        const data = await this.api('/api/counters/seed', {
          method: 'POST',
          body: JSON.stringify({
            visitor_id: this.getVisitorId(),
            items: ids.map((id) => ({
              id,
              base_views: this.counters[id].views,
              base_likes: this.counters[id].likes,
            })),
          }),
        });
        this.remote = true;
        ids.forEach((id) => {
          const remote = data && data[id];
          const state = this.counters[id];
          if (!remote || state.pending) return;
          state.views = remote.views;
          state.likes = remote.likes;
          state.liked = !!remote.liked;
          this.renderCounts(id, state.views, state.likes, state.liked);
        });
      } catch (e) {
        this.remote = false;
      }
    }

    renderCounts(id, views, likes, liked) {
      this.querySelectorAll('[data-reel-views="' + id + '"]').forEach((el) => {
        el.textContent = this.formatCount(views);
      });
      this.querySelectorAll('[data-reel-likes="' + id + '"]').forEach((el) => {
        el.textContent = this.formatCount(likes);
      });
      this.querySelectorAll('[data-reel-like][data-id="' + id + '"]').forEach((btn) => {
        btn.classList.toggle('is-liked', !!liked);
        btn.setAttribute('aria-pressed', liked ? 'true' : 'false');
      });
    }

    // One view per browser session per video (sessionStorage guard), so
    // replaying the same video does not inflate the counter. The guard is only
    // stored once the backend confirmed the view: a failed call must not use up
    // the session's only view. `viewing` blocks duplicate calls while one is
    // in flight.
    async registerView(slide) {
      const id = slide.getAttribute('data-id');
      const seenKey = 'mreel-seen2:' + id;
      try {
        if (sessionStorage.getItem(seenKey)) return;
      } catch (e) {}
      if (!this.viewing) this.viewing = new Set();
      if (this.viewing.has(id)) return;
      this.viewing.add(id);
      try {
        await this.ready;
        const state = this.counters[id];
        if (!this.remote || !state || !this.isRemoteId(id)) {
          console.warn('[video-reel] view not sent', { id, remote: this.remote, hasState: !!state });
          return;
        }
        const data = await this.api('/api/counters/' + encodeURIComponent(id) + '/view', { method: 'POST' });
        try {
          sessionStorage.setItem(seenKey, '1');
        } catch (e) {}
        state.views = data.views;
        this.renderCounts(id, state.views, state.likes, state.liked);
      } catch (e) {
        console.warn('[video-reel] view request failed', id, e);
      } finally {
        this.viewing.delete(id);
      }
    }

    async toggleLike(slide, likeBtn) {
      const id = slide.getAttribute('data-id');
      const state = this.counters[id];
      if (!state || state.pending) return;
      const prev = { likes: state.likes, liked: state.liked };
      const nextLiked = !state.liked;
      state.liked = nextLiked;
      state.likes = nextLiked ? state.likes + 1 : Math.max(0, state.likes - 1);
      this.renderCounts(id, state.views, state.likes, state.liked);

      state.pending = true;
      try {
        await this.ready;
        if (this.remote && this.isRemoteId(id)) {
          try {
            const data = await this.api('/api/counters/' + encodeURIComponent(id) + '/like', {
              method: 'POST',
              body: JSON.stringify({ visitor_id: this.getVisitorId(), liked: nextLiked }),
            });
            state.likes = data.likes;
          } catch (e) {
            state.likes = prev.likes;
            state.liked = prev.liked;
            this.renderCounts(id, state.views, state.likes, state.liked);
            return;
          }
        }
        try {
          localStorage.setItem('mreel:' + id + ':liked', state.liked ? '1' : '0');
        } catch (e) {}
        this.renderCounts(id, state.views, state.likes, state.liked);
      } finally {
        state.pending = false;
      }
    }

    addToCart(btn) {
      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;
      const defaultText = btn.textContent;
      btn.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      })
        .then((res) => {
          btn.textContent = res.ok ? btn.getAttribute('data-added-label') || defaultText : btn.getAttribute('data-error-label') || defaultText;
          document.dispatchEvent(new CustomEvent('cart-update'));
        })
        .catch(() => {
          btn.textContent = btn.getAttribute('data-error-label') || defaultText;
        })
        .finally(() => {
          setTimeout(() => {
            btn.textContent = defaultText;
            btn.disabled = false;
          }, 1800);
        });
    }

    syncControls(slide) {
      const video = slide.querySelector('[data-reel-video]');
      if (!video) return;
      slide.classList.toggle('is-paused', video.paused);
      const muteBtn = slide.querySelector('[data-reel-mute]');
      if (muteBtn) {
        muteBtn.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
        muteBtn.setAttribute('aria-label', muteBtn.getAttribute(video.muted ? 'data-label-unmute' : 'data-label-mute'));
      }
      const playBtn = slide.querySelector('[data-reel-playpause]');
      if (playBtn) {
        playBtn.setAttribute('aria-label', playBtn.getAttribute(video.paused ? 'data-label-play' : 'data-label-pause'));
      }
    }

    togglePlay(video) {
      if (video.paused) {
        this.safePlay(video);
      } else {
        video.pause();
      }
    }

    // AbortError = a newer pause()/play() replaced this call, not a real failure.
    // Videos always start muted; sound is opt-in through the mute button.
    safePlay(video) {
      const promise = video.play();
      if (!promise || typeof promise.catch !== 'function') return;
      promise.catch(() => {});
    }

    pauseAllVideos(except) {
      this.slides.forEach((slide) => {
        const video = slide.querySelector('[data-reel-video]');
        if (video && video !== except) video.pause();
      });
    }

    openModal(index) {
      if (!this.modal || typeof this.modal.showModal !== 'function') return;
      this.modal.showModal();
      if (this.track && this.slides[index]) {
        this.track.scrollTo({ left: this.slideScrollLeft(index), behavior: 'instant' in window ? 'instant' : 'auto' });
      }
      this.setActiveSlide(index);
    }

    // Scroll offset that centers a slide in the track (offsetLeft is relative
    // to the positioned modal, not the track, so it cannot be used directly).
    slideScrollLeft(index) {
      const slide = this.slides[index];
      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      return slideRect.left - trackRect.left + this.track.scrollLeft - (trackRect.width - slideRect.width) / 2;
    }

    step(dir) {
      if (!this.track) return;
      const nextIndex = Math.min(Math.max((this.activeIndex || 0) + dir, 0), this.slides.length - 1);
      const target = this.slides[nextIndex];
      if (!target) return;
      this.track.scrollTo({ left: this.slideScrollLeft(nextIndex), behavior: 'smooth' });
      this.setActiveSlide(nextIndex);
    }

    setActiveSlide(index) {
      const target = this.slides[index];
      if (!target) return;
      this.activeIndex = index;
      const video = target.querySelector('[data-reel-video]');
      this.pauseAllVideos(video);
      if (!video) return;
      video.currentTime = 0;
      video.muted = true;
      this.safePlay(video);
    }

    // On wide screens several slides are >=60% visible at once, so the active
    // one must be the slide closest to the track center.
    centeredSlideIndex() {
      const trackRect = this.track.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;
      let best = -1;
      let bestDistance = Infinity;
      this.slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      return best;
    }

    setupActiveSlideObserver() {
      if (!this.track || !this.slides.length || typeof IntersectionObserver === 'undefined') return;
      let settleTimer = null;
      const observer = new IntersectionObserver(
        () => {
          if (!this.modal || !this.modal.open) return;
          clearTimeout(settleTimer);
          settleTimer = setTimeout(() => {
            if (!this.modal.open) return;
            const index = this.centeredSlideIndex();
            if (index !== -1 && index !== this.activeIndex) this.setActiveSlide(index);
          }, 180);
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((slide) => observer.observe(slide));
    }

    setupBubble() {
      const bubbleVideo = this.querySelector('[data-reel-bubble-video]');
      if (!bubbleVideo || !this.slides.length) return;
      const randomIndex = Math.floor(Math.random() * this.slides.length);
      const source = this.slides[randomIndex].querySelector('[data-reel-video]');
      const src = source && source.getAttribute('data-src');
      if (src) {
        bubbleVideo.setAttribute('src', src);
      }
      const bubbleTrigger = this.querySelector('[data-reel-open]');
      if (bubbleTrigger) bubbleTrigger.setAttribute('data-reel-open', String(randomIndex));
      bubbleVideo.play().catch(() => {});
    }

    setupStripControls() {
      if (!this.strip) return;
      const prev = this.querySelector('[data-reel-strip-prev]');
      const next = this.querySelector('[data-reel-strip-next]');
      if (!prev || !next) return;
      const update = () => {
        const max = this.strip.scrollWidth - this.strip.clientWidth;
        prev.hidden = max <= 4 || this.strip.scrollLeft <= 4;
        next.hidden = max <= 4 || this.strip.scrollLeft >= max - 4;
      };
      const scrollBy = (dir) => {
        const thumb = this.strip.querySelector('[data-reel-open]');
        const cardWidth = thumb ? thumb.offsetWidth + 12 : 160;
        const count = Math.max(1, Math.floor(this.strip.clientWidth / cardWidth) - 1);
        this.strip.scrollBy({ left: dir * cardWidth * count, behavior: 'smooth' });
      };
      prev.addEventListener('click', () => scrollBy(-1));
      next.addEventListener('click', () => scrollBy(1));
      this.strip.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      if (typeof ResizeObserver !== 'undefined') new ResizeObserver(update).observe(this.strip);
      update();
    }

    setupAutoplayStrip() {
      if (!this.strip) return;
      const interval = parseInt(this.getAttribute('data-autoplay-interval'), 10);
      if (!interval) return;
      let timer = null;
      const start = () => {
        stop();
        timer = setInterval(() => {
          const cardWidth = this.strip.querySelector('[data-reel-open]') ? this.strip.querySelector('[data-reel-open]').offsetWidth + 12 : 160;
          const atEnd = this.strip.scrollLeft + this.strip.clientWidth >= this.strip.scrollWidth - 4;
          this.strip.scrollTo({ left: atEnd ? 0 : this.strip.scrollLeft + cardWidth, behavior: 'smooth' });
        }, interval);
      };
      const stop = () => {
        if (timer) clearInterval(timer);
      };
      start();
      this.strip.addEventListener('mouseenter', stop);
      this.strip.addEventListener('mouseleave', start);
      this.strip.addEventListener('touchstart', stop, { passive: true });
    }
  }

  customElements.define('video-reel-widget', VideoReelWidget);
}
